import { type FC } from 'react';
import cn from 'classnames';
import { HStack, Link, Sidebar, SidebarSection } from '@direct-frontend/components';
import { useLocation } from '@direct-frontend/modules-router';
import { betaSrc } from '#shared/assets';
import { CompanyLogo } from '#shared/components/CompanyLogo';
import { useExperimentFlag } from '#shared/lib/flags';
import { rootRoute } from '#shared/lib/routes';
import { useThemeColor } from '#shared/lib/theme';
import { checkIsYCPBrand } from '#shared/lib/ycp';
import { type DashboardSidebarProps, type MenuSection } from './lib/types';
import { SidebarMenuItem } from '../SidebarMenuItem';
import styles from './DashboardSidebar@desktop.module.css';

const DashboardSidebar = (props: DashboardSidebarProps) => {
    const { menuSections, sticky = false, isOpen = false, onToggleIsOpen } = props;

    const location = useLocation();
    const locationPathname = location.pathname;

    const enableAbHealthyCheck = useExperimentFlag('enable_ab_healthy_check', 'exp');
    const themeColor = useThemeColor();

    const isYCPBrand = checkIsYCPBrand();

    const companyLogo = (
        <CompanyLogo
            className={styles.logo}
            width={isYCPBrand ? 75 : 140}
            height={isYCPBrand ? 24 : 40}
            alt="Логотип"
            theme={themeColor}
        />
    );

    return (
        <div className={cn({ [styles.sticky]: sticky })} data-testid="DashboardSidebar">
            <Sidebar
                isOpen={isOpen}
                onToggleIsOpen={(value: boolean) => onToggleIsOpen?.(value)}
                withoutCollapsed
                logo={{
                    default: (
                        <Link className={styles.logoLink} href={rootRoute.getUrl()}>
                            {isYCPBrand ? (
                                <HStack
                                    className={styles.ycpLogoContainer}
                                    alignItems="center"
                                    justifyContent="flex-start"
                                >
                                    {companyLogo}
                                    {/* eslint-disable-next-line @direct-frontend/components/no-restricted-native-elements -- автоматический игнор в рамках DIRECT-327349 */}
                                    <img src={betaSrc} width={16} height={16} alt="Beta" className={styles.betaSign} />
                                </HStack>
                            ) : (
                                companyLogo
                            )}
                            {enableAbHealthyCheck && (
                                <div style={{ display: 'none' }} aria-hidden>
                                    Включен флаг enable_ab_healthy_check
                                </div>
                            )}
                        </Link>
                    ),
                }}
                widthClassName={styles.sidebar}
            >
                {menuSections.map((section, index) => (
                    <RenderSection key={index.toString()} locationPathname={locationPathname} section={section} />
                ))}
            </Sidebar>
        </div>
    );
};

const RenderSection: FC<{ locationPathname: string; section: MenuSection }> = ({ locationPathname, section }) => {
    if (section.length === 0) {
        return null;
    }

    return (
        <SidebarSection>
            {section.map((menuItem) => (
                <SidebarMenuItem key={menuItem.label} menuItem={menuItem} locationPathname={locationPathname} />
            ))}
        </SidebarSection>
    );
};

export default DashboardSidebar;
